import cadex, { Base_Uuid } from '@cadexchanger/web-toolkit'
import { TreeViewVisitor } from '../TreeViewVisitor'
import { ModelUserDefinedSceneDisplayer } from '../ModelUserDefinedSceneDisplayer'
import {
  ModelLoadedDataReturn,
  ModelNode,
  SceneElementsContext,
  UseSceneElementsProps,
} from '../types'

const populateTreeModel = async (
  model: cadex.ModelData_Model,
  loadResult: ModelLoadedDataReturn,
  setTreeModel: (map: Map<Base_Uuid, ModelNode>) => void
) => {
  const aVisitor = new TreeViewVisitor(loadResult)
  await model.accept(aVisitor)

  const treeModel = aVisitor.nodes.reduce(
    (map, item) => map.set(item.id, item) && map,
    new Map<Base_Uuid, ModelNode>()
  )

  let processId

  return (id: Base_Uuid, view3dObject: cadex.ModelPrs_View3dObject) => {
    if (treeModel.has(id)) {
      const item = treeModel.get(id)
      treeModel.set(id, Object.assign(item, { view3dObject }))
    }

    clearTimeout(processId)
    processId = setTimeout(() => {
      setTreeModel(treeModel)
    }, 700)
  }
}

const renderPMIData = async (
  model: ModelLoadedDataReturn,
  scene: cadex.ModelPrs_Scene
) => {
  const aPMIFactory = new cadex.ModelPrs_PMIFactory()
  const sceneGraphElement = model.roots[0]
  const modelRoots = model.roots

  if (!sceneGraphElement?.pmi) {
    return
  }

  const pmiViews = await sceneGraphElement.pmi.views()
  if (!pmiViews.length) {
    return
  }

  const newView = pmiViews?.find((x) => x.name === pmiViews[0].name)
  const pmiDataItems = await sceneGraphElement.pmi.pmiDataItems()
  const pmiViewObjects = await aPMIFactory.create(
    sceneGraphElement.pmi,
    modelRoots
  )

  newView.elements.forEach((theElement) => {
    const anElementIndex = pmiDataItems.findIndex(
      (theItem) => theElement === theItem.graphicalElement
    )

    if (anElementIndex !== -1) {
      scene.display(
        pmiViewObjects[anElementIndex],
        cadex.ModelPrs_DisplayMode.ShadedWithBoundaries
      )
    }
  })
}

export const buildUseSceneElementsContext =
  (
    { dataLoader, reference, modelName }: UseSceneElementsProps,
    getContext: () => SceneElementsContext
  ) =>
  () => {
    const { model, treeModel, scene, viewPort, setTreeModel, setViewPort } =
      getContext()

    const bindViewPort = () => {
      const vp = new cadex.ModelPrs_ViewPort(
        {
          showViewCube: true,
          autoZFitAll: true,
          tabIndex: 0,
        },
        reference.current as HTMLDivElement
      )

      return vp
    }

    const loadAndDisplayModel = async (modelPath: string) => {
      const loadResult = await model.loadFile(modelPath, dataLoader, false)

      await renderPMIData(loadResult, scene)

      const treeModelMapper = await populateTreeModel(
        model,
        loadResult,
        setTreeModel
      )

      const [displayMode, repMode] = loadResult.hasBRepRep
        ? [
            cadex.ModelPrs_DisplayMode.ShadedWithBoundaries,
            cadex.ModelData_RepresentationMask.ModelData_RM_BRep,
          ]
        : [
            cadex.ModelPrs_DisplayMode.Shaded,
            cadex.ModelData_RepresentationMask.ModelData_RM_Poly,
          ]

      scene.globalDisplayMode = displayMode

      const params = new cadex.ModelPrs_DisplayParams(
        new ModelUserDefinedSceneDisplayer(scene, treeModelMapper),
        displayMode,
        new cadex.ModelData_RepresentationMaskSelector(repMode),
        undefined,
        false,
        false
      )

      // params.interpretPolyVertexesAsBRepVertexes = true

      await cadex.ModelPrs_DisplayerApplier.apply(loadResult.roots, [], params)
    }

    const load = async () => {
      try {
        const vp = bindViewPort()
        vp.hoverEnabled = true
        scene.globalSelectionMode = cadex.ModelPrs_SelectionMode.Shape

        vp.attachToScene(scene)

        await loadAndDisplayModel(modelName)
        vp.fitAll()

        setViewPort(vp)
      } catch (err) {
        // eslint-disable-next-line
        console.log('Unable to load and display model: ', err)
      }
    }

    const context = {
      model,
      scene,
      viewPort,
      treeModel,
      setViewPort,
      setTreeModel,
      load,
    }

    return context
  }
